import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { signInWithPopup, signOut as firebaseSignOut } from "firebase/auth";
import { firebaseAuth, googleProvider } from "@/lib/firebase";
import api, { formatApiError } from "@/lib/api";
import { logError } from "@/lib/log";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ban, setBan] = useState(null);
  const [ipBan, setIpBan] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setBan(data.ban || null);
      setIpBan(null);
      return data;
    } catch (e) {
      setUser(null);
      if (e.response?.data?.detail?.code !== "ip_banned") setBan(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const onBanned = (e) => setBan(e.detail || { message: "Your account is suspended." });
    const onIpBanned = (e) => { setIpBan(e.detail || { message: "Access from this network is blocked." }); setUser(null); };
    const onRefresh = () => refresh();
    window.addEventListener("yaprio:banned", onBanned);
    window.addEventListener("yaprio:ip-banned", onIpBanned);
    window.addEventListener("yaprio:refresh-me", onRefresh);
    return () => {
      window.removeEventListener("yaprio:banned", onBanned);
      window.removeEventListener("yaprio:ip-banned", onIpBanned);
      window.removeEventListener("yaprio:refresh-me", onRefresh);
    };
  }, [refresh]);

  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const register = useCallback(async (email, username, password) => {
    try {
      const { data } = await api.post("/auth/register", { email, username, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const googleLogin = useCallback(async () => {
    try {
      const result = await signInWithPopup(firebaseAuth, googleProvider);
      const idToken = await result.user.getIdToken(true);
      const { data } = await api.post("/auth/firebase-google", { id_token: idToken });
      setUser(data);
      setBan(null);
      return { ok: true, user: data };
    } catch (e) {
      // If the Firebase popup was cancelled/closed, keep the UI quiet.
      const code = e?.code || "";
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        return { ok: false, cancelled: true };
      }
      return { ok: false, error: formatApiError(e.response?.data?.detail || e.message) };
    }
  }, []);

  const setUsername = useCallback(async (username) => {
    try {
      const { data } = await api.post("/auth/set-username", { username });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      logError("AuthContext", e);
    }
    try {
      await firebaseSignOut(firebaseAuth);
    } catch (e) {
      // Email/password accounts may not have an active Firebase session.
    }
    setUser(null);
    setBan(null);
    setIpBan(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, ban, ipBan, loading, refresh, login, register, googleLogin, setUsername, logout }),
    [user, ban, ipBan, loading, refresh, login, register, googleLogin, setUsername, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
